import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getProductFeedPreview, previewProductFeed } from "../../../utils/APIs";
import { useStatus } from "./common";
import { POLL_MS } from "./constants";
import { errorMessage } from "./helpers";

const secondsBetween = (from, to) => ((new Date(to).getTime() - new Date(from).getTime()) / 1000).toFixed(1);

// The feed's preview, owned by the editor so switching tabs doesn't lose it.
// The server builds it in the background and hands the result over once; this
// hook's state is then the only copy, gone when the editor closes.
export const usePreview = ({ auth, feedId }) => {
    const [job, setJob] = useState(null);
    const [result, setResult] = useState(null);
    const [draftChanged, setDraftChanged] = useState(false);
    const [validation, setValidation] = useState(null);
    const [starting, setStarting] = useState(false);
    const [status, showStatus, clearStatus] = useStatus(15000);

    const building = job?.status === "running";

    // Reads the preview job. `announce` reports a build that just finished.
    const loadPreview = useCallback(async ({ announce = false } = {}) => {
        try {
            const response = await getProductFeedPreview({ ...auth, feedId });
            const data = response?.data || {};
            setJob(data.job || null);
            if (data.job?.status === "running") return;
            if (data.result) {
                setResult(data.result);
                setDraftChanged(!!data.draft_changed);
                if (data.job.warnings) setValidation({ errors: [], warnings: data.job.warnings });
            }
            // The server only sends a message the first time it finds a result gone.
            const justExpired = data.job?.status === "expired" && !!data.job.message;
            if (!data.job || (!announce && !justExpired)) return;
            const seconds = secondsBetween(data.job.started_at, data.job.finished_at);
            if (data.job.status === "failed") showStatus(`The preview failed after ${seconds}s: ${data.job.message || "unknown error"}`, "error");
            else if (justExpired) showStatus(data.job.message, "error");
            else if (data.result?.outcome === "failed") showStatus(`The preview failed after ${seconds}s: ${data.result.message || "a required source failed"}.`, "error");
            else if (data.result) showStatus(`Preview built in ${seconds}s: ${data.result.rows?.live ?? 0} products, ${data.result.rows?.invalid ?? 0} invalid rows.`);
        } catch (e) {
            if (announce) showStatus(errorMessage(e), "error");
        }
    }, [auth.token, auth.organizationId, feedId]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    useEffect(() => {
        if (!building) return undefined;
        const timer = setInterval(() => loadPreview({ announce: true }), POLL_MS);
        return () => clearInterval(timer);
    }, [building, loadPreview]);

    // Closing or reloading the browser tab mid-build would lose the result.
    useEffect(() => {
        if (!building) return undefined;
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [building]);

    const build = async ({ dirty, saveDraft, productLimit }) => {
        if (dirty) {
            showStatus("Saving the draft first...", "info");
            if (!(await saveDraft({ quiet: true }))) {
                showStatus("The draft couldn't be saved, so the preview wasn't built.", "error");
                return;
            }
        }
        try {
            setStarting(true);
            const response = await previewProductFeed({ ...auth, feedId, productLimit });
            setJob(response?.data?.job || null);
            setValidation(response?.data?.job?.warnings ? { errors: [], warnings: response.data.job.warnings } : null);
            if (response?.data?.already_running) showStatus("A preview of this feed is already building; its result appears here when it finishes.", "info");
            else clearStatus();
        } catch (e) {
            setValidation(e?.data?.validation || null);
            const message = errorMessage(e);
            showStatus(e?.data?.validation?.errors?.length ? `${message} See the list below.` : message, "error");
            toast.error(message);
        } finally {
            setStarting(false);
        }
    };

    return { job, result, draftChanged, validation, starting, building, status, build };
};
